import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { handleError, successToast, errorToast } from "../../../utils/common";

const UpdateInvoiceModal = ({
  updateInvoice,
  setUpdateInvoice,
  invoiceUrl,
  activeCreditUsageId,
  callBack,
  accountDetailsChecked,
  accountNumber,
  ifscCode,
  setAccountNumber,
  setIfscCode,
  distributorName,
  payableAmount,
}: any) => {
  const privateInstance = useAxiosInstance();

  const [action, setAction] = useState<"approved" | "rejected" | "">("");
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submitDecision = async (decision: "approved" | "rejected") => {
    if (decision !== "rejected" && accountDetailsChecked === "No") {
      if (!accountNumber?.trim()) {
        errorToast("Please enter account number");
        return;
      }
      if (!ifscCode?.trim()) {
        errorToast("Please enter IFSC code");
        return;
      }
    }

    if (decision === "rejected" && !rejectReason.trim()) {
      errorToast("Please enter rejection reason");
      return;
    }

    setAction(decision);
    setLoading(true);

    const payload = {
      credit_usage_id: activeCreditUsageId,
      status: decision,
      reject_reason: decision === "rejected" ? rejectReason : null,
      metric_type: "credit_review_update",
      ...(accountDetailsChecked === "No" && {
        supplier_bank_account: accountNumber,
        supplier_ifsc: ifscCode.toUpperCase(),
      }),
    };

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V2}/analytics/digital-payments/overview`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        successToast("Invoice updated successfully");
        setUpdateInvoice(false);
        callBack?.();
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={updateInvoice}
      onClose={() => setUpdateInvoice(false)}
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setUpdateInvoice(false)}
      >
        <Typography variant="h5">Invoice Review</Typography>
      </CustomDialogTitle>

      <DialogContent>
        <Typography mb={1}>
          <strong>₹{payableAmount}/-</strong> to be disbursed to{" "}
          <strong>{distributorName}</strong>.{" "}
          {accountDetailsChecked === "Yes"
            ? "Please verify the details, amount and account details against the uploaded invoice."
            : "Please verify the details, amount and fill account details mentioned on the uploaded invoice."}
        </Typography>

        {/* Invoice Preview */}
        <Box mb={3}>
          <Typography variant="h6" mb={1}>
            Invoice Preview
          </Typography>

          {invoiceUrl ? (
            invoiceUrl.endsWith(".pdf") ? (
              <iframe
                src={invoiceUrl}
                title="Invoice PDF"
                width="100%"
                height="450px"
                style={{ border: "1px solid #ddd" }}
              />
            ) : (
              <Box
                component="img"
                src={invoiceUrl}
                alt="Invoice"
                onClick={() => window.open(invoiceUrl, "_blank")}
                sx={{
                  width: "100%",
                  maxHeight: 450,
                  objectFit: "contain",
                  border: "1px solid #ddd",
                }}
              />
            )
          ) : (
            <Typography color="error">Invoice not available</Typography>
          )}
        </Box>

        {/* ACCOUNT DETAILS SECTION */}
        {action !== "rejected" && (
          <Box mb={3}>
            <Typography variant="h6" mb={1}>
              Account Details
            </Typography>

            <>
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "row" }}>
                <TextField
                  fullWidth
                  id="account_no"
                  label="Account Number"
                  size="small"
                  value={
                    accountDetailsChecked === "No"
                      ? accountNumber || ""
                      : accountNumber
                        ? `${accountNumber}`
                        : ""
                  }
                  sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                  disabled={accountDetailsChecked === "Yes"}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />

                <TextField
                  fullWidth
                  id="ifsc_code"
                  label="IFSC Code"
                  size="small"
                  value={
                    accountDetailsChecked === "No"
                      ? ifscCode || ""
                      : ifscCode || ""
                  }
                  sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                  disabled={accountDetailsChecked === "Yes"}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                />
              </Box>
            </>
          </Box>
        )}

        {/* ACTION SECTION */}

        {/* Step 1: Show Approve / Reject buttons */}
        {action === "" && (
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <LoadingButton
              color="success"
              variant="contained"
              loading={loading}
              disabled={loading}
              onClick={() => submitDecision("approved")}
            >
              Approve
            </LoadingButton>

            <LoadingButton
              color="error"
              variant="contained"
              onClick={() => setAction("rejected")}
            >
              Reject
            </LoadingButton>
          </Box>
        )}

        {action === "rejected" && (
          <>
            <TextField
              label="Rejection Reason"
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 3 }}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />

            <Box textAlign="center" mt={3}>
              <LoadingButton
                variant="contained"
                color="primary"
                loading={loading}
                disabled={loading}
                onClick={() => submitDecision("rejected")}
              >
                Submit
              </LoadingButton>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpdateInvoiceModal;
